import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TFFTypography from '../../styled-components/TFFTypography';
import TFFCard from '../../styled-components/TFFCard';
import TFFButton from '../../styled-components/TFFButton';
import TFFDialog from '../../styled-components/TFFDialog';
import TFFAlert from '../../styled-components/TFFAlert';
import TFFTextInput from '../../styled-components/TFFTextInput';
import TFFSelectInput from '../../styled-components/TFFSelectInput';
import { useReimport } from '../../hooks/use-reimport';
import { SystemCode, SourceMarket } from '@tff/types/tff/integrate-flight-service';
import { Product } from '@tff/types/tff/retrieve';
import { SYSTEM_CODES, SOURCE_MARKETS } from '../../constants/integrate-flight-service';

type NdcxProduct = Extract<Product, 'atcomres' | 'atcomnr'>;

interface NdcxImportCardProps {
  setViewDialog: (value: boolean) => void;
}

const NdcxImportCard: React.FC<NdcxImportCardProps> = ({ setViewDialog }) => {
  const [recordLocator, setRecordLocator] = useState('');
  const [system, setSystem] = useState<SystemCode | ''>('');
  const [sourceMarket, setSourceMarket] = useState<SourceMarket | ''>('');
  const [officeId, setOfficeId] = useState('');
  const [clientPnr, setClientPnr] = useState('');
  const [product, setProduct] = useState<NdcxProduct | ''>('');
  const [bookingType, setBookingType] = useState('');
  const [promotionCode, setPromotionCode] = useState('');
  const [promotionName, setPromotionName] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  const navigate = useNavigate();
  const { setReimportRequest, reimportStatus, reimportResponse } = useReimport();

  useEffect(() => {
    if (reimportStatus === 'CONFIRMED' && reimportResponse) {
      setIsImporting(false);
      setShowSuccess(true);
      setTimeout(() => {
        navigate(`/tff/order/${recordLocator}`);
      }, 2000);
    } else if (reimportStatus === 'FAILURE') {
      setIsImporting(false);
      setShowErrorDialog(true);
    }
  }, [reimportStatus, reimportResponse, navigate, recordLocator]);

  useEffect(() => {
    setShowErrorDialog(false);
  }, [
    recordLocator,
    system,
    sourceMarket,
    officeId,
    clientPnr,
    product,
    bookingType,
    promotionCode,
    promotionName,
  ]);

  const handleImport = () => {
    if (!sourceMarket || !system || !product) return;

    setIsImporting(true);
    setShowErrorDialog(false);
    const additionalClientDetails = {
      ...(bookingType && { packageType: bookingType }),
      ...(promotionCode && { promotionCode }),
      ...(promotionName && { promotionName }),
    };

    setReimportRequest({
      recordLocator,
      systemCode: system,
      sourceMarket,
      clientPnr,
      product,
      ...(Object.keys(additionalClientDetails).length > 0 && { additionalClientDetails }),
    });
  };

  const handleBack = () => {
    setViewDialog(false);
  };

  const handleErrorCancel = () => {
    setShowErrorDialog(false);
    navigate('/tff/order/search');
  };

  const handleErrorRetry = () => {
    setShowErrorDialog(false);
    setShowSuccess(false);
  };

  const isRecordLocatorValid = !recordLocator || /^[A-Z0-9]{6}$/.test(recordLocator);
  const isFormValid = recordLocator && isRecordLocatorValid && system && sourceMarket && officeId && clientPnr && product;

  return (
    <TFFCard>
      <div className="tff-ndcx-import-card-content">
        {isImporting && (
          <div className="tff-ndcx-import-alert-margin">
            <TFFAlert variant="info" text="Importing the PNR..." />
          </div>
        )}
        {showSuccess && (
          <div className="tff-ndcx-import-alert-margin">
            <TFFAlert variant="success" text="✓ Import successful! Redirecting to order page..." />
          </div>
        )}
        <TFFTypography text="IMPORT MANUAL NDCX PNR" variant="lead" size="large" fontWeight="bold" />

        <div className="tff-ndcx-import-grid-three-columns">
          <TFFTextInput
            inputId="recordLocator"
            labelText="Record Locator *"
            value={recordLocator}
            onChange={e => setRecordLocator(e.target.value.toUpperCase())}
            placeholder="ABC123"
            required
            error={!isRecordLocatorValid}
            errorText="Format: 6 alphanumeric characters (e.g., ABC123)"
          />

          <TFFSelectInput
            id="system"
            label="System *"
            value={system}
            onChange={e => setSystem(e.target.value as SystemCode)}
            required
          >
            <option value="" disabled>
              -- Select an option --
            </option>
            {SYSTEM_CODES.map(code => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </TFFSelectInput>

          <TFFSelectInput
            id="sourceMarket"
            label="Source Market *"
            value={sourceMarket}
            onChange={e => setSourceMarket(e.target.value as SourceMarket)}
            required
          >
            <option value="" disabled>
              -- Select an option --
            </option>
            {SOURCE_MARKETS.map(({ code, label }) => (
              <option key={code} value={code}>
                {code} - {label}
              </option>
            ))}
          </TFFSelectInput>
        </div>

        <div className="tff-ndcx-import-grid-three-columns">
          <TFFTextInput
            inputId="officeId"
            labelText="Office Id *"
            value={officeId}
            onChange={e => setOfficeId(e.target.value)}
            required
          />

          <TFFTextInput
            inputId="clientPnr"
            labelText="AtCom-Record-Locator *"
            value={clientPnr}
            onChange={e => setClientPnr(e.target.value)}
            required
          />

          <TFFSelectInput
            id="product"
            label="Product *"
            value={product}
            onChange={e => setProduct(e.target.value as NdcxProduct)}
            required
          >
            <option value="" disabled>
              -- Select an option --
            </option>
            <option value="atcomres">atcomres</option>
            <option value="atcomnr">atcomnr</option>
          </TFFSelectInput>
        </div>

        <div className="tff-ndcx-import-section-margin">
          <TFFTypography text="Additional Client Details" variant="lead" size="medium" fontWeight="bold" />
        </div>

        <div className="tff-ndcx-import-grid-three-columns">
          <TFFSelectInput
            id="bookingType"
            label="Booking Type"
            value={bookingType}
            onChange={e => setBookingType(e.target.value)}
          >
            <option value="" disabled>
              -- Select an option --
            </option>
            <option value="Package">Package</option>
            <option value="FlightOnly">FlightOnly</option>
          </TFFSelectInput>

          <TFFTextInput
            inputId="promotionCode"
            labelText="Promotion Code"
            value={promotionCode}
            onChange={e => setPromotionCode(e.target.value)}
          />

          <TFFTextInput
            inputId="promotionName"
            labelText="Promotion Name"
            value={promotionName}
            onChange={e => setPromotionName(e.target.value)}
          />
        </div>

        <TFFTypography
          text="Fields marked with * are required."
          variant="base"
          size="small"
          extraStyle={{ marginTop: '24px', color: '#666' }}
        />

        <div className="tff-ndcx-import-button-container">
          <TFFButton buttonText="Back" variant="secondary" handleClick={handleBack} outline />
          <TFFButton buttonText="Import" variant="primary" handleClick={handleImport} disabled={!isFormValid} />
        </div>
      </div>

      <TFFDialog
        isOpen={showErrorDialog}
        onCancel={handleErrorCancel}
        dialogId="reimportErrorDialog"
        header={<TFFTypography variant="lead" size="large" fontWeight="semibold" text="PNR Import Failed" />}
      >
        <div className="tff-ndcx-import-dialog-content">
          <TFFTypography
            text="We couldn't import the PNR due to a technical error. Please try again or contact support if the issue persists."
            variant="base"
            size="medium"
          />
        </div>
        <div className="tff-ndcx-import-dialog-buttons">
          <TFFButton buttonText="Cancel" variant="secondary" handleClick={handleErrorCancel} outline />
          <TFFButton buttonText="Retry" variant="primary" handleClick={handleErrorRetry} />
        </div>
      </TFFDialog>
    </TFFCard>
  );
};

export default NdcxImportCard;
